import React, { useState, useRef, useEffect } from "react";
import "./SimpleDropdown.css";

/**
 * A labelled dropdown with an inline search box, single-select or multi-select.
 *
 * Single-select puts the chosen option's label on the closed control and closes
 * the panel on a pick. Multi-select gives every option a checkbox, keeps the
 * panel open afterwards — a visit there is usually a multi-pick one — and offers
 * a "Select All" row that acts on the current search filter rather than on every
 * option, so a filtered "all" means what it says.
 *
 * A multi-select's closed control shows the first `maxDisplayItems` picks as
 * chips and the rest as one "+N" chip, so a long selection stays one tidy line
 * instead of a run-on list that pushes its neighbours off the toolbar. A
 * selection covering every option collapses to "All selected" instead, which
 * says the same thing in less room than chipping the lot.
 *
 * The dropdown panel matches {@link MultiSelectDropdown}'s so the two read as
 * one control in a toolbar that mixes them.
 * @param {object} props the control's configuration.
 * @param {string} [props.label] text shown beside the control; omitted renders none.
 * @param {Array<{value: *, label: string}|string|number>} [props.options] the
 *        options to offer; a bare value stands as its own label.
 * @param {*|Array} props.value the selected value, or the array of them when `isMulti`.
 * @param {Function} props.onChange called with the new value, or with the new
 *        array when `isMulti`.
 * @param {boolean} [props.isAlert] styles the control as needing attention.
 * @param {string} [props.placeholder] closed-control text while nothing is picked.
 * @param {boolean} [props.disabled] stops the panel opening.
 * @param {number} [props.minWidth] narrowest the control may render, in px.
 * @param {number} [props.maxWidth] widest the control may render, in px.
 * @param {object} [props.style] extra styles for the wrapper.
 * @param {string} [props.searchPlaceholder] placeholder for the search box.
 * @param {boolean} [props.isMulti] turns on multi-select.
 * @param {boolean} [props.showSelectAll] offers the "Select All" row; multi-select only.
 * @param {number} [props.maxDisplayItems] how many picks are chipped before the
 *        rest collapse into the "+N" chip.
 * @returns {JSX.Element} the dropdown.
 */
function SimpleDropdown({
  label,
  options = [],
  value,
  onChange,
  isAlert = false,
  placeholder,
  disabled = false,
  minWidth = 120,
  maxWidth,
  style = {},
  searchPlaceholder = "Search...",
  isMulti = false,
  showSelectAll = true,
  maxDisplayItems = 3,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "object" ? { value: opt.value, label: opt.label } : { value: opt, label: opt }
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedValues = isMulti ? (Array.isArray(value) ? value : []) : [];

  const selectedOption = !isMulti ? normalizedOptions.find((opt) => String(opt.value) === String(value)) : null;

  const isAllSelected =
    isMulti &&
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedValues.some((v) => String(v) === String(opt.value)));

  const selectedLabels = isMulti
    ? normalizedOptions
      .filter((opt) => selectedValues.some((v) => String(v) === String(opt.value)))
      .map((opt) => opt.label)
    : [];

  const isEverythingSelected =
    isMulti && selectedValues.length === normalizedOptions.length && normalizedOptions.length > 1;

  /** Chips are the multi-select's own display; every other case is one line of text. */
  const showsChips = isMulti && selectedLabels.length > 0 && !isEverythingSelected;

  const getHeaderText = () => {
    if (isMulti) {
      if (selectedValues.length === 0) return placeholder || "Select options...";
      if (isEverythingSelected) return "All selected";
      return selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder || "Select options...";
    }
    return selectedOption ? selectedOption.label : placeholder || "Select...";
  };
  const headerText = getHeaderText();

  const visibleLabels = selectedLabels.slice(0, Math.max(1, maxDisplayItems));
  const overflowCount = selectedLabels.length - visibleLabels.length;

  const hasSelection = isMulti ? selectedValues.length > 0 : !!selectedOption;

  /** Opens or closes the panel, clearing the search term on the way out. */
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((o) => !o);
    if (isOpen) setSearchTerm("");
  };

  /**
   * Toggles one option. Multi-select stays open afterwards, since a visit there
   * is usually a multi-pick one.
   * @param {*} val the value of the option that was clicked.
   */
  const handleSelectOption = (val) => {
    if (isMulti) {
      const exists = selectedValues.some((v) => String(v) === String(val));
      const next = exists
        ? selectedValues.filter((v) => String(v) !== String(val))
        : [...selectedValues, val];
      onChange(next);
      return;
    }
    onChange(val);
    setIsOpen(false);
    setSearchTerm("");
  };

  /**
   * Selects or clears every option the current search leaves visible.
   *
   * The merged selection is mapped back through the options so values keep their
   * original types rather than becoming the strings used to compare them.
   */
  const handleSelectAll = () => {
    if (isAllSelected) {
      const filteredValues = new Set(filteredOptions.map((o) => String(o.value)));
      onChange(selectedValues.filter((v) => !filteredValues.has(String(v))));
    } else {
      const merged = new Set([...selectedValues.map(String), ...filteredOptions.map((o) => String(o.value))]);
      onChange(normalizedOptions.filter((o) => merged.has(String(o.value))).map((o) => o.value));
    }
  };

  const accent = isAlert ? "#f59e0b" : "#3f88a5";
  const border = isOpen ? accent : isAlert ? "#f59e0b" : "#e2e8f0";
  const textColor = isAlert ? "#b45309" : hasSelection ? "#1e293b" : "#94a3b8";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, ...style }}>
      {label && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", whiteSpace: "nowrap" }}>
          {label}
        </span>
      )}
      <div ref={containerRef} style={{ position: "relative", minWidth, maxWidth }}>
        <div
          onClick={handleToggle}
          title={showsChips ? headerText : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 10px",
            borderRadius: 8,
            border: `1.5px solid ${border}`,
            background: isAlert ? "#fffbeb" : "#fff",
            fontSize: 12,
            fontWeight: 600,
            color: textColor,
            cursor: disabled ? "not-allowed" : "pointer",
            outline: "none",
            minWidth,
            maxWidth,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            opacity: disabled ? 0.7 : 1,
            minHeight: 20,
          }}
        >
          {showsChips ? (
            <span className="sdd-chips">
              {visibleLabels.map((chipLabel) => (
                <span key={chipLabel} className="sdd-chip">{chipLabel}</span>
              ))}
              {overflowCount > 0 && (
                <span className="sdd-chip sdd-chip--overflow">+{overflowCount}</span>
              )}
            </span>
          ) : (
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {headerText}
            </span>
          )}
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              color: isAlert ? "#b45309" : "#94a3b8",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              pointerEvents: "none",
            }}
          >
            ▾
          </span>
        </div>

        {isOpen && !disabled && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 999,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              maxHeight: 250,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                borderBottom: "1px solid #e2e8f0",
                outline: "none",
                fontSize: 13,
                background: "#f8fafc",
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                boxSizing: "border-box",
              }}
            />
            <div style={{ overflowY: "auto", padding: "4px 0", flex: 1 }}>
              {!isMulti && placeholder && (
                <div
                  onClick={() => handleSelectOption("")}
                  style={{
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "#94a3b8",
                    background: !value ? "#f0f7ff" : "transparent",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = !value ? "#f0f7ff" : "transparent")}
                >
                  {placeholder}
                </div>
              )}

              {isMulti && showSelectAll && filteredOptions.length > 0 && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1e293b",
                    borderBottom: "1px solid #e2e8f0",
                    background: isAllSelected ? "#f0f7ff" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    style={{ marginRight: 8, accentColor: "#3f88a5", cursor: "pointer" }}
                  />
                  Select All
                </label>
              )}

              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const isSelected = isMulti
                    ? selectedValues.some((v) => String(v) === String(opt.value))
                    : String(opt.value) === String(value);
                  return isMulti ? (
                    <label
                      key={opt.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#1e293b",
                        background: isSelected ? "#f0f7ff" : "transparent",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#f0f7ff" : "transparent")}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectOption(opt.value)}
                        style={{ marginRight: 8, accentColor: "#3f88a5", cursor: "pointer" }}
                      />
                      {opt.label}
                    </label>
                  ) : (
                    <div
                      key={opt.value}
                      onClick={() => handleSelectOption(opt.value)}
                      style={{
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#1e293b",
                        fontWeight: isSelected ? 700 : 400,
                        background: isSelected ? "#f0f7ff" : "transparent",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#f0f7ff" : "transparent")}
                    >
                      {opt.label}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SimpleDropdown;